import {
  ActionIcon,
  Flex,
  Grid,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import CustomText from "components/ui/CustomText/CustomText";
import { ChangeEvent, memo, useCallback } from "react";
import { POItemFormValues } from "../../config";
import { calcItemTotal, formatCurrency } from "../../utils";

type ProductOption = { label: string; value: string; disabled?: boolean };
type POItemField = keyof POItemFormValues;
type POEditableField = "quantity" | "unitPrice" | "productName";

interface PurchaseOrderItemRowProps {
  index: number;
  item: POItemFormValues;
  itemError?: Partial<Record<POItemField, string>>;
  itemTouched?: Partial<Record<POItemField, boolean>>;
  productOptions: ProductOption[];
  onProductChange: (index: number, productId: string | null) => void;
  onFieldChange: (
    index: number,
    field: POEditableField,
    value: number | string,
  ) => void;
  onFieldBlur: (index: number, field: POItemField) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
}

const spanProduct = { base: 12, md: 3 };
const spanName = { base: 12, md: 3 };
const spanUnit = { base: 4, md: 2 };
const spanQty = { base: 4, md: 2 };
const spanPrice = { base: 4, md: 2 };

const PurchaseOrderItemRow = memo(
  ({
    index,
    item,
    itemError,
    itemTouched,
    productOptions,
    onProductChange,
    onFieldChange,
    onFieldBlur,
    onRemove,
    canRemove,
  }: PurchaseOrderItemRowProps) => {
    const itemTotal = calcItemTotal(item.quantity, item.unitPrice);

    const getItemError = useCallback(
      (field: POItemField) =>
        itemTouched?.[field] && itemError?.[field] ? itemError[field] : null,
      [itemError, itemTouched],
    );

    const handleProductChange = useCallback(
      (productId: string | null) => onProductChange(index, productId),
      [onProductChange, index],
    );
    const handleProductBlur = useCallback(
      () => onFieldBlur(index, "productId"),
      [onFieldBlur, index],
    );
    const handleProductNameChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) =>
        onFieldChange(index, "productName", event.currentTarget.value),
      [onFieldChange, index],
    );
    const handleProductNameBlur = useCallback(
      () => onFieldBlur(index, "productName"),
      [onFieldBlur, index],
    );
    const handleQuantityChange = useCallback(
      (value: number | string) => onFieldChange(index, "quantity", value),
      [onFieldChange, index],
    );
    const handleQuantityBlur = useCallback(
      () => onFieldBlur(index, "quantity"),
      [onFieldBlur, index],
    );
    const handleUnitPriceChange = useCallback(
      (value: number | string) => onFieldChange(index, "unitPrice", value),
      [onFieldChange, index],
    );
    const handleUnitPriceBlur = useCallback(
      () => onFieldBlur(index, "unitPrice"),
      [onFieldBlur, index],
    );
    const handleRemove = useCallback(() => onRemove(index), [onRemove, index]);

    return (
      <Stack gap="xs">
        <Flex align="center" justify="space-between">
          <Flex align="center" gap={10}>
            <CustomText variant="product-label">
              {`Sản phẩm ${index + 1}`}
            </CustomText>
            <ActionIcon
              color="red"
              variant="subtle"
              onClick={handleRemove}
              disabled={!canRemove}
              aria-label="Xóa dòng"
            >
              <IconTrash size={14} />
            </ActionIcon>
          </Flex>

          <Flex align="center" gap={6}>
            <Text size="sm" c="dimmed">Thành tiền:</Text>
            <Text fw={700} size="sm">{formatCurrency(itemTotal)}</Text>
          </Flex>
        </Flex>

        <Grid align="flex-start">
          <Grid.Col span={spanProduct}>
            <Select
              label={index === 0 ? "Sản phẩm" : undefined}
              placeholder="Chọn sản phẩm"
              withAsterisk={index === 0}
              data={productOptions}
              value={item.productId || null}
              onChange={handleProductChange}
              onBlur={handleProductBlur}
              error={getItemError("productId")}
              searchable
            />
          </Grid.Col>

          <Grid.Col span={spanName}>
            <TextInput
              label={index === 0 ? "Tên trên đơn" : undefined}
              placeholder="Tên hiển thị trên đơn và phiếu in"
              value={item.productName}
              onChange={handleProductNameChange}
              onBlur={handleProductNameBlur}
              error={getItemError("productName")}
            />
          </Grid.Col>

          <Grid.Col span={spanUnit}>
            <TextInput
              label={index === 0 ? "Đơn vị" : undefined}
              placeholder="—"
              readOnly
              value={item.productUnit || ""}
            />
          </Grid.Col>

          <Grid.Col span={spanQty}>
            <NumberInput
              label={index === 0 ? "Số lượng" : undefined}
              placeholder="0"
              withAsterisk={index === 0}
              min={1}
              value={item.quantity}
              onChange={handleQuantityChange}
              onBlur={handleQuantityBlur}
              error={getItemError("quantity")}
            />
          </Grid.Col>

          <Grid.Col span={spanPrice}>
            <NumberInput
              label={index === 0 ? "Đơn giá" : undefined}
              placeholder="0"
              withAsterisk={index === 0}
              min={0}
              thousandSeparator=","
              value={item.unitPrice}
              onChange={handleUnitPriceChange}
              onBlur={handleUnitPriceBlur}
              error={getItemError("unitPrice")}
            />
          </Grid.Col>
        </Grid>
      </Stack>
    );
  },
);

PurchaseOrderItemRow.displayName = "PurchaseOrderItemRow";

export default PurchaseOrderItemRow;
